import CircleByIdButton from '@/circle/components/CircleByIdButton'
import CollabEditor from '@/editor/components/CollabEditor'
import MemberButton from '@/member/components/MemberButton'
import { Card, CardBody, CardHeader, Flex, Text, Wrap } from '@chakra-ui/react'
import { MemberSummaryFragment } from '@gql'
import { RoleReviewRole } from '@rolebase/shared/model/roleReview'
import React from 'react'
import { useTranslation } from 'react-i18next'

interface Props {
  // Stable per person or role: the collaborative document and the stored text
  docId: string
  // Who speaks (by person) or which role is reviewed (by role)
  member?: MemberSummaryFragment
  roles: RoleReviewRole[]
  holders?: MemberSummaryFragment[]
  value: string
  readOnly?: boolean
  onSave(value: string): void
}

// One card of the role review (#80): a person with their roles, or a role
// with its holders, and the text said for it.
export default function MeetingRoleReviewItem({
  docId,
  member,
  roles,
  holders,
  value,
  readOnly,
  onSave,
}: Props) {
  const { t } = useTranslation()

  return (
    <Card boxShadow="none" bg="transparent" mb={3}>
      <CardHeader px={0} pb={2}>
        <Flex align="center" gap={2} wrap="wrap">
          {member ? (
            <>
              <MemberButton member={member} size="sm" />
              <Wrap spacing={1}>
                {roles.map((row) => (
                  <CircleByIdButton
                    key={row.circle.id}
                    id={row.circle.id}
                    size="xs"
                  />
                ))}
              </Wrap>
            </>
          ) : (
            <>
              {roles[0] && (
                <CircleByIdButton id={roles[0].circle.id} size="sm" />
              )}
              <Flex flex={1} justify="flex-end" gap={1} wrap="wrap">
                {holders && holders.length > 0 ? (
                  holders.map((holder) => (
                    <MemberButton key={holder.id} member={holder} size="xs" />
                  ))
                ) : (
                  <Text fontSize="sm" color="gray.500">
                    {t('MeetingRoleReview.nobody')}
                  </Text>
                )}
              </Flex>
            </>
          )}
        </Flex>
      </CardHeader>
      <CardBody px={0} pt={0}>
        <CollabEditor
          docId={docId}
          value={value}
          placeholder={
            member
              ? t('MeetingRoleReview.placeholderMember', { name: member.name })
              : t('MeetingRoleReview.placeholderRole')
          }
          readOnly={readOnly}
          saveEvery={10000}
          onSave={onSave}
        />
      </CardBody>
    </Card>
  )
}
